/**
 * API-Client der Leitwarte (/api/leitwarte/*).
 *
 * Die Leitwarte ist Nicos Master-Dashboard (24.09.2026): EINE dunkle Seite
 * statt Heute, Vertriebsanalyse, Statistiken und Finanzen-Übersicht. Sie
 * zeigt, was der Nachtlauf gefunden hat (Vorgänge), Zusagen an Kunden, die
 * Kunden-Signal-Matrix, den Finanzplan, und auf dem zweiten Bildschirm die
 * Datenlandschaft über alle Systeme.
 *
 * Diese Datei ist der VERTRAG zwischen Seite und Endpunkten: Die Typen hier
 * sind genau das, was die Endpunkte liefern.
 */

import { supabase } from './supabase';

// ── Grundtypen ───────────────────────────────────────────────────────────────

export type Prio = 'rot' | 'gelb' | 'blau';
export type VorgangStatus = 'offen' | 'freigegeben' | 'spaeter' | 'erledigt' | 'verworfen' | 'in_arbeit';
export type VorgangTyp = 'auffaelligkeit' | 'vorschlag' | 'entwurf' | 'nachfass' | 'zusage' | 'frist' | 'termin' | 'technik' | 'geld' | 'info' | 'aufgabe';
export type Autopilot = 'melden' | 'vorbereiten' | 'selbst';
/** Zustand einer Signal-Zelle in der Kunden-Matrix. */
export type Zustand = 'ok' | 'beobachten' | 'handeln' | 'kritisch' | 'bau' | 'keine';

export interface Signal {
  zustand: Zustand;
  /** Kurztext für die Zelle, z. B. „3 aktiv, 543 €" oder „94, gesund". */
  text: string;
}

export interface MailEntwurf {
  art: 'mail_entwurf';
  an: string;
  betreff: string;
  text: string;
  /** Lokale Pfade vorbereiteter Anhänge (Entwürfe, Grafiken, PDFs). */
  anhaenge?: string[];
}
/** Vorbereitete Arbeit als Dateien: Grafiken, Texte, PDFs, lokale Commits. */
export interface Datei {
  art: 'datei';
  dateien: Array<{ pfad: string; beschreibung?: string }>;
  hinweis?: string;
}
export interface Aenderung {
  art: 'aenderung';
  /** z. B. 'meta_ads', 'google_ads', 'publivo', 'lms', 'repo' */
  system: string;
  beschreibung: string;
  payload?: Record<string, unknown>;
}
export interface Leitfaden {
  art: 'leitfaden';
  titel: string;
  punkte: string[];
}
/** Ein Auftrag an einen der Skills (finom-angebot, projektvorbereitung, …), den der Auftragslauf ausführt. */
export interface Auftrag {
  art: 'auftrag';
  skill: string;
  auftrag: string;
  kunde?: string | null;
  /** Vom Lauf gefüllt: was gemacht wurde, welche Dateien entstanden sind. */
  bericht?: string | null;
  dateien?: Array<{ pfad: string; beschreibung?: string }>;
}
export type Artefakt = MailEntwurf | Aenderung | Leitfaden | Datei | Auftrag | { art: string; [k: string]: unknown };

export interface Vorgang {
  id: string;
  created_at: string;
  updated_at: string;
  typ: VorgangTyp;
  prioritaet: Prio;
  status: VorgangStatus;
  autopilot: Autopilot;
  titel: string;
  beschreibung: string | null;
  vorschlag: string | null;
  quelle: string;
  quelle_ref: string | null;
  beleg: Record<string, unknown>;
  artefakt: Artefakt | null;
  customer_id: string | null;
  betrieb_id: string | null;
  project_id: string | null;
  faellig_am: string | null;
  wiedervorlage_am: string | null;
  entschieden_at: string | null;
  entscheidung: string | null;
  erledigt_at: string | null;
  /** Vom Endpunkt aufgelöst. */
  kunde_name: string | null;
  /** Knopf-Beschriftungen, die der Nachtlauf mitgibt: [ja, spaeter, nein]. */
  knoepfe: string[] | null;
}

export interface Zusage {
  id: string;
  created_at: string;
  updated_at: string;
  titel: string;
  beschreibung: string | null;
  customer_id: string | null;
  betrieb_id: string | null;
  zugesagt_am: string | null;
  quelle: string | null;
  quelle_ref: string | null;
  bedingung: string | null;
  faellig_am: string | null;
  status: 'offen' | 'wartet' | 'erledigt' | 'verworfen';
  erledigt_at: string | null;
  gruppe: string | null;
  kunde_name: string | null;
}

export interface NotizFolge {
  art: 'zusage_neu' | 'zusage_status' | 'vorgang_neu' | 'auftrag' | 'hinweis';
  text: string;
  angewendet?: boolean;
  ref?: string | null;
  daten?: Record<string, unknown>;
}

export interface Notiz {
  id: string;
  created_at: string;
  text: string;
  quelle: string;
  status: 'neu' | 'zugeordnet' | 'bestaetigt' | 'verworfen';
  zuordnung: { customer_id?: string | null; betrieb_id?: string | null; project_id?: string | null; typ?: string; zusammenfassung?: string; kunde_name?: string | null } | null;
  folgen: NotizFolge[] | null;
  verarbeitet_at: string | null;
  bestaetigt_at: string | null;
}

export interface Lauf {
  id: string;
  name: string;
  gestartet_at: string;
  beendet_at: string | null;
  status: 'laeuft' | 'ok' | 'fehler';
  zusammenfassung: string | null;
  fehler: string | null;
  quelle: string;
}

/** Zeile in der „Läuft"-Liste: echte Läufe UND die bestehenden Vercel-Crons über ihre synced_at-Spuren. */
export interface LaufZeile {
  name: string;
  zuletzt: string | null;
  status: 'ok' | 'fehler' | 'unbekannt' | 'laeuft';
  hinweis: string | null;
}

// ── Übersicht (Bildschirm 1) ─────────────────────────────────────────────────

export interface GeldMonat {
  monat: string;        // '2026-10'
  label: string;        // 'Okt'
  einnahmen: number;    // Euro
  ausgaben: number;     // Kosten + Steuerrücklage, Euro
  kontostand: number;   // Euro, mit Töpfen
  frei: number;         // Euro, frei verfügbar
  ust_zahlung: number;  // Euro
  geplant: boolean;     // enthält geplante Einmalbeträge
}

export interface KundeZeile {
  id: string;
  name: string;
  slug: string;
  anfragen30: number;
  anfragen_davor30: number;
  ads: Signal;
  website: Signal;
  publivo: Signal;
  zahlung: Signal;
  kontakt: { text: string; tage: number | null };
}

export interface Uebersicht {
  jetzt: string;
  letzter_lauf: Lauf | null;
  kpis: {
    mrr_euro: number;
    /** Monat, für den mrr_euro gilt, z. B. 'September'. */
    mrr_monat_label?: string;
    /** Aufschlüsselung der festen Einnahmen dieses Monats, z. B. 8 Publivo-Abos 2.232 €. */
    mrr_teile?: Array<{ label: string; anzahl: number; euro: number }>;
    /** Abgleich Finanzplan gegen die Live-Quellen (Publivo-Abos, Stripe-Produkte). */
    mrr_live_hinweis?: string | null;
    /** Alle Einnahmen laut Plan im nächsten Monat, mit Projektrechnungen. */
    einnahmen_naechster_monat_euro?: number;
    naechster_monat_label?: string;
    mrr_plan: Array<{ monat: string; euro: number }>;
    mrr_plan_hinweis: string | null;
    konto_euro: number;
    konto_datum: string | null;
    ust_topf_euro: number;
    anfragen30: number;
    anfragen_davor30: number;
    publivo: { zahlend: number; trial: number; trial_enden_7t: number; abgelaufen: number; naechste: Array<{ name: string; endet: string }> };
    vorgaenge: { offen: number; rot: number; gelb: number; blau: number; vorbereitet: number };
  };
  kunden: KundeZeile[];
  geld: { monate: GeldMonat[]; hinweise: string[] };
  anfragen_heatmap: { matrix: number[][]; gesamt: number; tage: number; max: number; hinweis: string | null };
  vertrieb: { kandidaten: number; briefe: number; angerufen: number; testphase: number; kunde: number; anrufe_faellig: number; hinweis: string | null };
  vorgaenge: Vorgang[];
  zusagen: Zusage[];
}

// ── Datenlandschaft (Bildschirm 2) ───────────────────────────────────────────

export interface Reihe { label: string; wert: number }
export interface Woche { woche: string; wert: number; wert2?: number }

export interface Daten {
  zeitraum_tage: number;
  besucher: {
    unique30: number;
    wochen: Woche[];                 // Seitenaufrufe je Woche
    stunden: number[];               // 24 Werte
    wochentage: number[];            // 7 Werte, Mo..So
    kanaele: Reihe[];
    geraete: Reihe[];
    je_kunde30: Reihe[];
  };
  google: {
    wochen: Woche[];                 // klicks (wert) + impressionen (wert2)
    top_queries: Array<{ query: string; klicks: number; impressionen: number }>;
    ads_terms: Array<{ term: string; klicks: number; conversions: number }>;
    hinweis: string | null;
  };
  anfragen: {
    gesamt180: number;
    ausgang: Reihe[];                // gewonnen / verloren / spam / unbekannt
    kanaele90: Reihe[];
    formulare90: Reihe[];
    heatmap: number[][];             // 7 x 12 (2-Stunden-Blöcke)
    wochentage: number[];
  };
  publivo: {
    verfuegbar: boolean;
    veroeffentlicht_gesamt: number;
    je_monat: Reihe[];
    stunden: number[];               // 24
    wochentage: number[];            // 7
    plattformen: Reihe[];
    formate: Reihe[];
    status: Reihe[];                 // review / scheduled / published / failed
    health: { wochen: string[]; orgs: Array<{ name: string; status: string; werte: Array<number | null>; aktuell: number | null }> };
    zernio30: Reihe[];
    mails: Array<{ template: string; gesendet: number; geoeffnet: number }>;
    engagement_gespeichert: boolean;
  };
  blog: {
    verfuegbar: boolean;
    aufrufe90: number;
    ki_treffer90: number;
    wochen: Woche[];
    leser: Reihe[];                  // Menschen + Agenten
    geraete: Reihe[];
    lesezeit_sek: number | null;
    scroll_pct: number | null;
  };
  potenzial: { checks: number; mit_email: number; berichte: number; gewerke: Reihe[] };
  luecken: string[];
}

// ── Live (Publivo-Datenzentrum gespiegelt) ───────────────────────────────────

export interface PublivoLive {
  verfuegbar: boolean;
  jetzt: string;
  online: Array<{ id: string; name: string; admin: boolean; org: string | null; pfad: string; zuletzt: string }>;
  activity: Array<{ id: string; aktion: string; typ: string | null; org: string | null; at: string; meta: Record<string, unknown> }>;
  /** Aktivitäten je Stunde der letzten 24 h, älteste zuerst. */
  stunden: number[];
  pipeline: { entwurf: number; generiert: number; review: number; freigegeben: number; geplant: number; heute: number; gesamt: number };
  plattformen: Reihe[];
  fehler: Array<{ id: string; titel: string | null; text: string | null; at: string; org: string | null }>;
  orgs: { gesamt: number; aktiv: number; trial: number };
}

// ── Aufrufe ──────────────────────────────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');
  return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
}

async function parseJson<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any).error || `Fehler (HTTP ${res.status})`);
  return json as T;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: await authHeaders() });
  return parseJson<T>(res);
}

async function send<T>(method: 'POST' | 'PATCH', path: string, body: unknown): Promise<T> {
  const res = await fetch(path, { method, headers: await authHeaders(), body: JSON.stringify(body) });
  return parseJson<T>(res);
}

export const getUebersicht = () => get<Uebersicht>('/api/leitwarte/uebersicht');
export const getDaten = () => get<Daten>('/api/leitwarte/daten');
export const getLaeufe = () => get<{ laeufe: Lauf[]; zeilen: LaufZeile[] }>('/api/leitwarte/laeufe');
export const getPublivoLive = () => get<PublivoLive>('/api/leitwarte/live');

export const listVorgaenge = (status?: VorgangStatus | 'alle') =>
  get<{ vorgaenge: Vorgang[] }>(`/api/leitwarte/vorgaenge${status ? `?status=${status}` : ''}`).then((r) => r.vorgaenge);

/** Entscheidung zu einem Vorgang: ja → freigegeben, spaeter → Wiedervorlage, nein → verworfen, erledigt → erledigt. */
export const entscheideVorgang = (id: string, entscheidung: 'ja' | 'spaeter' | 'nein' | 'erledigt', notiz?: string, wiedervorlage_am?: string) =>
  send<{ vorgang: Vorgang }>('PATCH', '/api/leitwarte/vorgaenge', { id, entscheidung, notiz, wiedervorlage_am }).then((r) => r.vorgang);

export const listZusagen = (status?: Zusage['status'] | 'alle') =>
  get<{ zusagen: Zusage[] }>(`/api/leitwarte/zusagen${status ? `?status=${status}` : ''}`).then((r) => r.zusagen);
export const patchZusage = (id: string, patch: Partial<Pick<Zusage, 'status' | 'faellig_am' | 'bedingung' | 'beschreibung' | 'titel'>>) =>
  send<{ zusage: Zusage }>('PATCH', '/api/leitwarte/zusagen', { id, ...patch }).then((r) => r.zusage);
export const neueZusage = (z: Partial<Zusage> & { titel: string }) =>
  send<{ zusage: Zusage }>('POST', '/api/leitwarte/zusagen', z).then((r) => r.zusage);

export const sendeNotiz = (text: string, quelle: 'web' | 'handy' = 'web') =>
  send<{ notiz: Notiz }>('POST', '/api/leitwarte/notizen', { text, quelle }).then((r) => r.notiz);
export const listNotizen = () => get<{ notizen: Notiz[] }>('/api/leitwarte/notizen').then((r) => r.notizen);
export const bestaetigeNotiz = (id: string, ok: boolean) =>
  send<{ notiz: Notiz }>('PATCH', '/api/leitwarte/notizen', { id, aktion: ok ? 'bestaetigen' : 'verwerfen' }).then((r) => r.notiz);

// ── Anzeige-Helfer (gemeinsam für Seite und Karten) ──────────────────────────

export const euro = (n: number, nachkomma = 0) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma }).format(n);

export const zahl = (n: number) => new Intl.NumberFormat('de-DE').format(n);

export const PRIO_FARBE: Record<Prio, string> = { rot: '#d03b3b', gelb: '#fab219', blau: '#3987e5' };
export const ZUSTAND_ZEICHEN: Record<Zustand, { zeichen: string; farbe: string; wort: string }> = {
  ok: { zeichen: '✓', farbe: '#0ca30c', wort: 'in Ordnung' },
  beobachten: { zeichen: '~', farbe: '#fab219', wort: 'beobachten' },
  handeln: { zeichen: '!', farbe: '#ec835a', wort: 'handeln' },
  kritisch: { zeichen: '✕', farbe: '#d03b3b', wort: 'kritisch' },
  bau: { zeichen: '●', farbe: '#3987e5', wort: 'im Bau' },
  keine: { zeichen: '', farbe: '#8a8a84', wort: 'nicht gebucht' },
};
